import { Fragment } from "react";

/**
 * A deliberately small markdown renderer for the legal documents (xmg-sec-opsdocs P1): `#`–`####` headings,
 * paragraphs, `-`/`*` and `1.` lists, `>` quotes, `**bold**`, `` `code` `` and `[text](url)` links. It builds
 * React elements and never sets inner HTML, so markup in a document renders as text. A link target must be
 * `https:`, `http:`, `mailto:` or a site path (`/privacy`); anything else (`javascript:`, `data:`) renders as plain
 * text. No dependency is added for this: the documents are owner-written prose, not arbitrary markdown.
 */

type Block =
  | { kind: "heading"; level: 1 | 2 | 3 | 4; text: string }
  | { kind: "paragraph"; text: string }
  | { kind: "quote"; text: string }
  | { kind: "list"; ordered: boolean; items: string[] };

const HEADING = /^(#{1,4})\s+(.*)$/;
const BULLET = /^\s*[-*]\s+(.*)$/;
const NUMBERED = /^\s*\d+\.\s+(.*)$/;
const QUOTE = /^>\s?(.*)$/;

function parseBlocks(source: string): Block[] {
  const blocks: Block[] = [];
  let paragraph: string[] = [];
  let quote: string[] = [];
  let list: { ordered: boolean; items: string[] } | null = null;

  const flush = (): void => {
    if (paragraph.length > 0) blocks.push({ kind: "paragraph", text: paragraph.join(" ") });
    if (quote.length > 0) blocks.push({ kind: "quote", text: quote.join(" ") });
    if (list !== null) blocks.push({ kind: "list", ...list });
    paragraph = [];
    quote = [];
    list = null;
  };

  for (const raw of source.split(/\r?\n/)) {
    const line = raw.trimEnd();
    if (line.trim() === "") {
      flush();
      continue;
    }
    const heading = HEADING.exec(line);
    if (heading !== null) {
      flush();
      blocks.push({
        kind: "heading",
        level: (heading[1] ?? "#").length as 1 | 2 | 3 | 4,
        text: heading[2] ?? "",
      });
      continue;
    }
    const bullet = BULLET.exec(line);
    const numbered = bullet === null ? NUMBERED.exec(line) : null;
    if (bullet !== null || numbered !== null) {
      const ordered = numbered !== null;
      const text = (bullet ?? numbered)?.[1] ?? "";
      if (list === null || list.ordered !== ordered) {
        flush();
        list = { ordered, items: [] };
      }
      list.items.push(text);
      continue;
    }
    const quoted = QUOTE.exec(line);
    if (quoted !== null) {
      if (paragraph.length > 0 || list !== null) flush();
      quote.push(quoted[1] ?? "");
      continue;
    }
    if (list !== null && /^\s+/.test(raw)) {
      // A wrapped list item: continuation lines are indented.
      const items: string[] = list.items;
      items[items.length - 1] = `${items[items.length - 1] ?? ""} ${line.trim()}`;
      continue;
    }
    if (list !== null || quote.length > 0) flush();
    paragraph.push(line.trim());
  }
  flush();
  return blocks;
}

const INLINE = /(\*\*([^*]+)\*\*)|(`([^`]+)`)|(\[([^\]]+)\]\(([^)\s]+)\))/g;
const SAFE_HREF = /^(https?:|mailto:|\/(?!\/))/i;

function renderInline(text: string): React.ReactNode[] {
  const nodes: React.ReactNode[] = [];
  let last = 0;
  let key = 0;
  for (const match of text.matchAll(INLINE)) {
    const start = match.index ?? 0;
    if (start > last) nodes.push(text.slice(last, start));
    if (match[2] !== undefined) {
      nodes.push(<strong key={key++}>{match[2]}</strong>);
    } else if (match[4] !== undefined) {
      nodes.push(<code key={key++}>{match[4]}</code>);
    } else {
      const label = match[6] ?? "";
      const href = match[7] ?? "";
      nodes.push(
        SAFE_HREF.test(href) ? (
          <a key={key++} href={href}>
            {label}
          </a>
        ) : (
          <Fragment key={key++}>{label}</Fragment>
        ),
      );
    }
    last = start + match[0].length;
  }
  if (last < text.length) nodes.push(text.slice(last));
  return nodes;
}

export function Markdown({ source }: { source: string }): JSX.Element {
  return (
    <>
      {parseBlocks(source).map((block, index) => {
        switch (block.kind) {
          case "heading": {
            const Tag = `h${block.level}` as const;
            return <Tag key={index}>{renderInline(block.text)}</Tag>;
          }
          case "quote":
            return <blockquote key={index}>{renderInline(block.text)}</blockquote>;
          case "list": {
            const items = block.items.map((item, itemIndex) => <li key={itemIndex}>{renderInline(item)}</li>);
            return block.ordered ? <ol key={index}>{items}</ol> : <ul key={index}>{items}</ul>;
          }
          default:
            return <p key={index}>{renderInline(block.text)}</p>;
        }
      })}
    </>
  );
}
